import moment from 'moment-timezone';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { createFilter } from 'react-select';

import { DATE_FORMAT_YEAR_MONTH_DAY, RPM_PATIENT_STATUS } from "../constants";
import { ORG_FEATURES, orgFeatureEnabled } from "./userPermissions";
import Strings from "../Strings";

// Patient medications as the medication editors use them (fill dates as moments), and back to the backend's format.
export const medicationsFromDTO = medications => (medications || []).map(m => ({
  ...m,
  fillDate: m.fillDate ? moment(m.fillDate) : '',
}));

export const medicationsToDTO = medications => medications?.map(m => ({
  ...m,
  fillDate: m.fillDate && m.fillDate.isValid ? m.fillDate.format() : null,
}));

const fieldTypes = {
  simpleField: 'simpleField',
  combinedField: 'combinedField',
  simpleArray: 'simpleArray',
  complexArray: 'complexArray',
};

export const patientFieldsEnum = {
  firstName: {
    fieldId: 'firstName',
    fieldType: fieldTypes.simpleField,
    required: () => { return true },
    toDTO: val => val?.trim() || val,
  },
  lastName: {
    fieldId: 'lastName',
    fieldType: fieldTypes.simpleField,
    required: () => { return true },
    toDTO: val => val?.trim() || val,
  },
  mrn: {
    fieldId: 'mrn',
    fieldType: fieldTypes.simpleField,
    required: () => { return false },
    toDTO: val => val?.trim() || val,
  },
  medicareBeneficiaryId: {
    fieldId: 'medicareBeneficiaryId',
    fieldType: fieldTypes.simpleField,
    required: (patient, stage, enrollment) => { 
      return enrollment?.accessRegistration && [RPM_PATIENT_STATUS.enrolled, RPM_PATIENT_STATUS.verified, RPM_PATIENT_STATUS.activated].includes(stage);
    },
    includeEmpty: true,
    toDTO: val => val?.trim() || val,
  },
  pharmacyPatientId: {
    fieldId: 'pharmacyPatientId',
    fieldType: fieldTypes.simpleField,
    required: () => { return false },
    toDTO: val => val?.trim() || val,
  },
  athenaPatientId: {
    fieldId: 'athenaPatientId',
    fieldType: fieldTypes.simpleField,
    required: () => { return false },
  },
  practiceId: {
    fieldId: 'practiceId',
    fieldType: fieldTypes.simpleField,
    required: () => { return false },
  },
  athenaDepartmentId: {
    fieldId: 'athenaDepartmentId',
    fieldType: fieldTypes.simpleField,
    required: () => { return false },
  },
  dateOfBirth: {
    fieldId: 'dateOfBirth',
    fieldType: fieldTypes.simpleField,
    required: () => { return true },
    toDTO: val => val && val.isValid() ? val.format(DATE_FORMAT_YEAR_MONTH_DAY) : null,
  },
  textPhoneNo: {
    fieldId: 'textPhoneNo',
    fieldType: fieldTypes.simpleField,
    required: (patient) => patient.consentForProvider === 'set',
    toDTO: val => val?.trim() || val,
  },
  email: {
    fieldId: 'email',
    fieldType: fieldTypes.simpleField,
    required: (patient) => { return patient?.last_login },
    toDTO: val => val?.trim() || val,
  },
  homeAddress: {
    fieldId: 'homeAddress',
    fieldType: fieldTypes.simpleField,
    required: () => { return false },
    toDTO: val => val?.trim() || val,
  },
  address: {
    fieldId: 'address',
    fieldType: fieldTypes.combinedField,
    required: (_, stage) => { return stage !== RPM_PATIENT_STATUS.onboarded },
    // required: (_p, _s, enrollment) => { return enrollment?.shipment_method === 'ship_to_patient' || enrollment?.shipment_method === 'shipped_by_pharmacy' },
  },
  facilityId : {
    fieldId: 'facilityId',
    fieldType: fieldTypes.simpleField,
    required: () => { return false },
  },

  insurance: {
    fieldId: 'insurance',
    fieldType: fieldTypes.complexArray,
    required: () => { return false },
  },

  centerDetails: {
    fieldId: 'centerDetails',
    fieldType: fieldTypes.complexArray,
    required: () => { return false },
  },

  conditions: {
    fieldId: 'conditions',
    fieldType: fieldTypes.simpleArray,
    required: () => { return false },
  },

  transplantDetails: {
    fieldId: 'transplantDetails',
    fieldType: fieldTypes.complexArray,
    toDTO: val => val.map(t => ({
      ... t,
      transplantDate: t.transplantDate && t.transplantDate.isValid ? t.transplantDate.format(DATE_FORMAT_YEAR_MONTH_DAY) : null,
    })
    ),
    required: () => { return false },
  },

  timezone: { fieldId: 'timezone', fieldType: fieldTypes.simpleField, required: () => { return false }},
  gender: { fieldId: 'gender', fieldType: fieldTypes.simpleField, required: () => { return true } },
  language: {
    fieldId: 'language',
    fieldType: fieldTypes.simpleField,
    required: () => { return false }
  },
  medications: {
    fieldId: 'medications',
    fieldType: fieldTypes.complexArray,
    required: () => { return false },
    toDTO: val => medicationsToDTO(val),
  },
  consentForProvider: {
    fieldId: 'consentForProvider',
    initialEnrollmentField: true,
    readOnly: (_, stage) => stage && (stage !== RPM_PATIENT_STATUS.verified),
    required: (_, stage) => stage && (stage !== RPM_PATIENT_STATUS.onboarded),
    toDTO: (val) => {
      const extendedOutreachStatusEnabled = orgFeatureEnabled(ORG_FEATURES.use_outreach_status);
      if (extendedOutreachStatusEnabled) {
        return undefined;
      }
      return val;
    },
  },
  outreachStatus: {
    fieldId: 'outreachStatus',
    initialEnrollmentField: true,
    readOnly: (_, stage) => stage && (stage !== RPM_PATIENT_STATUS.verified && stage !== RPM_PATIENT_STATUS.enrolled),
    required: (_, stage) => stage && (stage !== RPM_PATIENT_STATUS.onboarded),
  },
  nextOutreachAt: {
    fieldId: 'nextOutreachAt',
    initialEnrollmentField: true,
    getPropertyForDTO: () => 'nextOutreachDate',
    required: () => false,
    toDTO: val => val && val.isValid() ? val.format() : null,
  },
  initiateEnrollmentByProviderAt: {
    fieldId: 'initiateEnrollmentByProviderAt',
    required: (p) => {return p?.televisitChecked},
    readOnly: (p, stage) => { return p.consentForProvider !== 'set' || stage !== RPM_PATIENT_STATUS.verified },
    toDTO: val => val && val.isValid() ? val.format() : null,
  },
  textOptOutStatus: {
    fieldId: 'textOptOutStatus',
    fieldType: fieldTypes.simpleField,
    required: () => { return true },
    toDTO: () => undefined,
  }
  // defaultShipmentMethod: {
  //   fieldId: 'defaultShipmentMethod',
  //   fieldType: fieldTypes.simpleField,
  //   readOnly: (_, stage) => stage && (stage !== RPM_PATIENT_STATUS.verified),
  //   required: () => { return false }
  // },
  // defaultRtmDeviceModel: {
  //   fieldId: 'defaultRtmDeviceModel',
  //   fieldType: fieldTypes.simpleField,
  //   readOnly: (_, stage) => stage && (stage !== RPM_PATIENT_STATUS.verified),
  //   required: () => { return false }
  // },
};

export const defaultShipmentMethod = 'ship_to_patient';

export const patientToDTO = (patient, { includeInitialEnrollment = false } = {}) => {
  return Object.values(patientFieldsEnum).reduce((acc, field) => {
    if (field.initialEnrollmentField && !includeInitialEnrollment) {
      return acc;
    }
    const value = patient[field.fieldId];
    const hasValue = value
      || (field.includeFalsy && value !== undefined && value !== null)
      || (field.includeEmpty && Object.prototype.hasOwnProperty.call(patient, field.fieldId));
    if (hasValue) {
      acc[field.getPropertyForDTO ? field.getPropertyForDTO(patient) : field.fieldId] = field.toDTO ? field.toDTO(value, patient) : value;
    }
    return acc;
  }, {});
};

export const checkIfPatientWasModified = (patient, originalPatient) => {
  return Object.values(patientFieldsEnum).filter(field => !field.initialEnrollmentField).some(field => {
    const value = field.toDTO ? field.toDTO(patient[field.fieldId]) : patient[field.fieldId];
    const originalValue = field.toDTO ? field.toDTO(originalPatient[field.fieldId]) : originalPatient[field.fieldId];
    return !isEqual(value, originalValue);
  });
}

// The patient's conditions (ids) as the condition pickers use them (patient.conditionsObjects): each org condition
// merged with the patient's details for it, or the patient's own details when the org list doesn't have it.
export const getConditionsObjects = (conditionIds, conditionDetails, orgConditions) => (conditionIds || []).map(id => {
  const condition = (orgConditions || []).find(c => c.id === id);
  const details = conditionDetails?.[id];
  return condition
    ? { ...condition, ...details, value: condition.id, label: condition.title }
    : { ...details, id, value: id, label: details?.title };
});

// Key a medication uses to reference one of patient.conditionsObjects: the org condition id,
// or the ICD code for a condition that is only created when the patient is saved.
export const conditionKey = condition => condition?.value ?? condition?.id;

// Search options for the condition pickers: matching org conditions first, then ICD-10 results the org does not have yet.
export const buildConditionOptions = (value, orgConditions, idcConditionsData) => {
  const idcConditions = (idcConditionsData || []).map(e => ({
    ...e,
    value: e.code,
    label: e.title,
  }));
  const search = value.toLowerCase();
  const matchingOrgConditions = (orgConditions || [])
    .filter(c => c.title?.toLowerCase().includes(search) || c.condition_icd10cm?.toLowerCase().includes(search))
    .map(c => ({ ...c, value: c.id, label: c.title, isDisabled: !!c.isRemovalBlockedByEnrollment }));
  return matchingOrgConditions.concat(idcConditions.filter(d => !(orgConditions || []).some(p => p.condition_icd10cm === d.code)));
};

export const createConditionOptionsLoader = (getIdcConditions, orgConditions) => debounce((value, callback) => {
  if (!value || value.length < 3) {
    callback([]);
    return;
  }
  getIdcConditions({ search: value }).then(resp => {
    callback(buildConditionOptions(value, orgConditions, resp.response?.data));
  });
}, 200);

// Loaded options are already matched server-side by title or ICD code; the default label/value filter would hide an org condition found by its code.
export const conditionFilterOption = createFilter({
  ignoreCase: true,
  ignoreAccents: true,
  matchFrom: 'any',
  stringify: option => `${option.data.label} ${option.data.value} ${option.data.condition_icd10cm || option.data.code || ''}`,
  trim: true,
});

const INSURANCE_FIELDS = ['insuranceName', 'policyId', 'groupNumber', 'payerId'];

// A row the user started on the Insurance section and then cleared (e.g. { insuranceName: '' }) is not an insurance.
export const hasInsuranceData = insurance => INSURANCE_FIELDS.some(field => `${insurance?.[field] ?? ''}`.trim());

// Insurances carry no id; a medication keeps its own copy, matched to the patient's list by these fields.
export const isSameInsurance = (a, b) => INSURANCE_FIELDS.every(field => (a?.[field] || '') === (b?.[field] || ''));

// Drops the given free-text fields when blank and trims them otherwise, so an untouched medication stays equal to the loaded one.
export const withoutBlankText = (item, keys) => keys.reduce((acc, key) => {
  const text = `${acc[key] ?? ''}`.trim();
  const { [key]: _removed, ...rest } = acc;
  return text ? { ...rest, [key]: text } : rest;
}, { ...item });

const normalizedNdc = ndc => `${ndc ?? ''}`.trim();
const normalizedName = name => `${name ?? ''}`.trim().toLowerCase();

// A schedule links to an entry of patient.medications by the entry's name and NDC (schedule format
// "patient_medication" / "ndc_code"); patient medications have no id and names may repeat.
export const isLinkedPatientMedication = (medication, patientMedication, ndcCode) =>
  !!patientMedication && patientMedication === medication?.name && normalizedNdc(ndcCode) === normalizedNdc(medication?.ndc);

// Whether another entry of patientMedications (all but the one at index) has the medication's name and NDC, i.e. a schedule
// link could not tell them apart. Bulk upload already merges such entries (name|NDC is its merge key). An edited entry
// (original = its stored version) that already shared them (older data) stays editable while its name and NDC are unchanged.
// An NDC is 8 (product code without package, e.g. 50580-449) to 11 digits, optionally hyphenated. RxNav's ndcproperties also resolves
// RxCUIs and SPL ids (id=209459 finds Tylenol), so only an NDC-shaped value may be looked up there.
export const isNdcShaped = ndc => /^[\d-]+$/.test(`${ndc ?? ''}`) && [8, 9, 10, 11].includes(`${ndc}`.replace(/-/g, '').length);

// A failed RxNav lookup: 400/404 mean RxNav rejected the id; anything else (timeout, rate limit, network, 5xx) means
// the NDC couldn't be checked, which must not block saving.
export const ndcLookupFailure = error => ([400, 404].includes(error?.response?.status) ? 'notFound' : 'unavailable');

// The distinct RxCUIs of an RxNav ndcproperties response, i.e. the drugs an NDC belongs to.
export const ndcRxcuis = data => uniq((data?.ndcPropertyList?.ndcProperty || []).map(p => p?.rxcui).filter(Boolean));

export const isDuplicatePatientMedication = (medication, patientMedications, index, original) =>
  !(original && isLinkedPatientMedication(original, medication?.name, medication?.ndc))
  && (patientMedications || []).some((m, i) => i !== index && isLinkedPatientMedication(m, medication?.name, medication?.ndc));

// Whether any of the patient's schedules (portal medications tab objects, with a raw "format") covers a patient medication.
// Linked schedules cover only their entry. Unlinked (older) schedules cover an entry with the same NDC, or with the same
// name when the NDCs don't conflict; no fuzzy matching, so a medication without a schedule is never hidden by a similar name.
export const isMedicationScheduled = (medication, schedules) => (schedules || []).some(schedule => {
  const format = schedule?.format || {};
  if (format.patient_medication) {
    return isLinkedPatientMedication(medication, format.patient_medication, format.ndc_code);
  }
  const scheduleNdc = normalizedNdc(format.ndc_code);
  const medicationNdc = normalizedNdc(medication?.ndc);
  if (scheduleNdc && medicationNdc) {
    return scheduleNdc === medicationNdc;
  }
  const name = normalizedName(medication?.name);
  return !!name && name === normalizedName(schedule?.medicationName);
});

// Links a schedule's medication block to patientMedications[index] (null = no link). SIG and notes that are empty or still hold
// the previously linked entry's values follow the link: they take the new entry's values, or are cleared when unlinking. Text the
// user typed is kept. Unlinking leaves the NDC alone: it still describes the scheduled drug.
export const linkPatientMedication = (medication, patientMedications, index) => {
  const current = medication || {};
  const previous = (patientMedications || [])
    .find(m => isLinkedPatientMedication(m, current.patient_medication, current.ndc_code));
  const next = index === null || index === undefined ? null : patientMedications?.[index];
  const filled = ['sig', 'notes'].reduce((acc, key) => {
    const value = current[key] || '';
    return !value || value === (previous?.[key] || '') ? { ...acc, [key]: next?.[key] || '' } : acc;
  }, {});
  if (!next) {
    return { ...current, ...filled, patient_medication: '' };
  }
  return {
    ...current,
    ...filled,
    patient_medication: next.name,
    // "" rather than undefined: the schedule update keeps a field that is not sent.
    ndc_code: next.ndc || '',
  };
};

export const MEDICATION_ROW_KINDS = {
  scheduled: 'scheduled',
  scheduleOnly: 'scheduleOnly',
  medicationOnly: 'medicationOnly',
};

// Rows of the staff Medications table: one per schedule (joined to its patient medication only through the explicit
// link, never a name guess), then one per patient medication no schedule covers (isMedicationScheduled, the set the
// "Other medications" table showed). Sorted by name.
export const buildMedicationRows = (schedules, patientMedications) => {
  const medications = (patientMedications || []).filter(m => m?.name);
  const scheduleRows = (schedules || []).map(schedule => {
    const format = schedule?.format || {};
    const patientMedication = medications.find(m => isLinkedPatientMedication(m, format.patient_medication, format.ndc_code));
    return {
      key: `schedule-${schedule.rootId}`,
      kind: patientMedication ? MEDICATION_ROW_KINDS.scheduled : MEDICATION_ROW_KINDS.scheduleOnly,
      name: schedule.medicationName || '',
      ndc: format.ndc_code || patientMedication?.ndc || '',
      schedule,
      patientMedication,
    };
  });
  const medicationRows = medications
    .map((patientMedication, index) => ({ patientMedication, index }))
    .filter(({ patientMedication }) => !isMedicationScheduled(patientMedication, schedules))
    .map(({ patientMedication, index }) => ({
      key: `medication-${index}`,
      kind: MEDICATION_ROW_KINDS.medicationOnly,
      name: patientMedication.name,
      ndc: patientMedication.ndc || '',
      patientMedication,
    }));
  return [...scheduleRows, ...medicationRows].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

const withFixedMedicationConditions = (patient, conditionIdsByKey) => (
  patient?.medications
    ? {
      medications: patient.medications.map(m => (
        m.conditions ? { ...m, conditions: m.conditions.map(key => conditionIdsByKey[key]).filter(Boolean) } : m
      )),
    }
    : {}
);

export const fixNewConditions = (patient, newConditions) => {
  if (!patient?.conditionsObjects || patient.conditionsObjects.length === 0) {
    return {
      ...patient,
      conditions: [],
      ...withFixedMedicationConditions(patient, {}),
    };
  }

  const conditionIdsByKey = {};
  const patientConditionIds = patient.conditionsObjects.map(c => {
    const id = c.id || newConditions.find(nc => nc.code === c.code)?.id;
    conditionIdsByKey[conditionKey(c)] = id;
    return id
  });

  return {
    ...patient,
    conditions: patientConditionIds,
    ...withFixedMedicationConditions(patient, conditionIdsByKey),
  }
}

export const OUTREACH_STATUS_ENUM = {
  'RPM Order Pending': {
    value: 'RPM Order Pending',
    label: Strings.extendedOutreachStatusEnum.rpmOrderPending,
    registration: true,
    showNextOutreachDate: true,
    movesPatientToState: RPM_PATIENT_STATUS.verified,
  },
  'RPM Order Approved': {
    value: 'RPM Order Approved',
    label: Strings.extendedOutreachStatusEnum.rpmOrderApproved,
    registration: true,
    showNextOutreachDate: true,
    movesPatientToState: RPM_PATIENT_STATUS.verified,
  },
  'Ineligible': {
    value: 'Ineligible',
    label: Strings.extendedOutreachStatusEnum.ineligible,
    registration: true,
    unregistration: true,
    showNextOutreachDate: false,
    movesPatientToState: RPM_PATIENT_STATUS.notEnrolled,
  },
  'Patient Outreach in Process': {
    value: 'Patient Outreach in Process',
    label: Strings.extendedOutreachStatusEnum.patientOutreachInProcess,
    unregistration: true,
    showNextOutreachDate: true,
  },
  'Deferred': {
    value: 'Deferred',
    label: Strings.extendedOutreachStatusEnum.deferred,
    unregistration: true,
    showNextOutreachDate: true,
  },
  'Patient Declined': {
    value: 'Patient Declined',
    label: Strings.extendedOutreachStatusEnum.patientDeclined,
    unregistration: true,
    showNextOutreachDate: false,
    movesPatientToState: RPM_PATIENT_STATUS.notEnrolled,
  },
  'Unable to Reach': {
    value: 'Unable to Reach',
    label: Strings.extendedOutreachStatusEnum.unableToReach,
    unregistration: true,
    showNextOutreachDate: false,
    movesPatientToState: RPM_PATIENT_STATUS.notEnrolled,
  },
  'RPM Order Declined': {
    value: 'RPM Order Declined',
    label: Strings.extendedOutreachStatusEnum.rpmOrderDeclined,
    unregistration: true,
    showNextOutreachDate: false,
    movesPatientToState: RPM_PATIENT_STATUS.notEnrolled,
  },
  'No Provider RPM Order Reponse': {
    value: 'No Provider RPM Order Reponse',
    label: Strings.extendedOutreachStatusEnum.noProviderRpmOrderReponse,
    unregistration: true,
    showNextOutreachDate: false,
    movesPatientToState: RPM_PATIENT_STATUS.notEnrolled,
  },
};
